import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Plus, AlertCircle, Loader2, Database } from 'lucide-react';
import { useFlowStore } from '../../store/FlowStateStore.ts';
import { Environment as environment } from '../../environment.ts';
import { toast } from 'react-toastify';

interface CreateAttributeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialName?: string;
    defaultType?: string;
    onCreated?: (name: string) => void;
}

const sanitizeAttributeName = (raw: string): string => {
    return raw
        .replace(/\s+/g, "_")
        .replace(/[^A-Za-z0-9_.-]/g, "");
};

const CreateAttributeDialog: React.FC<CreateAttributeDialogProps> = ({ isOpen, onClose, initialName, defaultType, onCreated }) => {
    const { businessId, fetchBusinessAttributes } = useFlowStore();
    const [name, setName] = useState(initialName ?? '');
    const [type, setType] = useState(defaultType ?? 'TEXT');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            setName(initialName ?? '');
            setType(defaultType ?? 'TEXT');
            setError(null);
        }
    }, [isOpen, initialName, defaultType]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Attribute name is required");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const payload = {
            businessId: businessId,
            attributeName: trimmed,
            attributeType: type,
            isGlobal: false
        };

        try {
            await axios.post(`${environment.baseUrl}/create/attribute`, payload);
            toast.success(`Attribute "${trimmed}" created successfully!`);
            await fetchBusinessAttributes();
            onCreated?.(trimmed);
            onClose();
            setName('');
            setType('TEXT');
        } catch (err: any) {
            console.error("Failed to create attribute:", err);
            setError(err.response?.data?.message || "Failed to create attribute. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return createPortal(
        <div className="attr-dialog-overlay" onClick={onClose}>
            <div className="attr-dialog-content" onClick={e => e.stopPropagation()}>
                <div className="attr-dialog-header">
                    <div className="title-group">
                        <div className="icon-box">
                            <Plus size={16} />
                        </div>
                        <h3>New Variable</h3>
                    </div>
                    <button onClick={onClose} className="close-btn">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="attr-dialog-body">
                    {error && (
                        <div className="error-alert">
                            <AlertCircle size={14} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="input-field">
                        <label>Variable Name</label>
                        <input
                            autoFocus
                            placeholder="e.g. user_age, order_id"
                            value={name}
                            onChange={(e) => setName(sanitizeAttributeName(e.target.value))}
                        />
                        <p className="hint">Spaces will be converted to underscores automatically.</p>
                    </div>

                    <div className="input-field">
                        <label>Data Type</label>
                        <div className="select-wrapper">
                            <select value={type} onChange={(e) => setType(e.target.value)}>
                                <option value="TEXT">Text / String</option>
                                <option value="NUMBER">Number</option>
                                <option value="BOOLEAN">Boolean (True/False)</option>
                                <option value="ARRAY">Array / List</option>
                                <option value="OBJECT">JSON Object</option>
                                <option value="DATE">Date</option>
                            </select>
                            <Database size={14} className="select-icon" />
                        </div>
                    </div>

                    <div className="attr-dialog-footer">
                        <button type="button" onClick={onClose} className="btn-cancel">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSubmitting} className="btn-submit">
                            {isSubmitting ? (
                                <><Loader2 size={16} className="spin" /> Creating...</>
                            ) : (
                                "Create Variable"
                            )}
                        </button>
                    </div>
                </form>

                <style>{`
                    .attr-dialog-overlay {
                        position: fixed;
                        inset: 0;
                        background: rgba(15, 23, 42, 0.6);
                        backdrop-filter: blur(4px);
                        z-index: 20000;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 20px;
                        animation: attrFadeIn 0.2s ease-out;
                    }

                    .attr-dialog-content {
                        background: white;
                        width: 100%;
                        max-width: 400px;
                        border-radius: 16px;
                        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                        overflow: hidden;
                        animation: attrZoomIn 0.2s ease-out;
                    }

                    @keyframes attrFadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes attrZoomIn { 
                        from { opacity: 0; transform: scale(0.95); } 
                        to { opacity: 1; transform: scale(1); } 
                    }

                    .attr-dialog-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 16px 20px;
                        border-bottom: 1px solid #f1f5f9;
                        background: #f8fafc;
                    }

                    .title-group {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .icon-box {
                        padding: 6px;
                        background: rgba(10, 71, 76, 0.1);
                        color: var(--primary-bg-color);
                        border-radius: 8px;
                        display: flex;
                    }

                    .attr-dialog-header h3 {
                        margin: 0;
                        font-size: 16px;
                        font-weight: 700;
                        color: #1e293b;
                    }

                    .close-btn {
                        background: none;
                        border: none;
                        padding: 6px;
                        color: #64748b;
                        cursor: pointer;
                        border-radius: 50%;
                        display: flex;
                        transition: all 0.2s;
                    }

                    .close-btn:hover { background: #e2e8f0; color: #1e293b; }

                    .attr-dialog-body {
                        padding: 20px;
                        display: flex;
                        flex-direction: column;
                        gap: 20px;
                    }

                    .error-alert {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 10px 12px;
                        background: #fef2f2;
                        border: 1px solid #fee2e2;
                        color: #dc2626;
                        border-radius: 8px;
                        font-size: 13px;
                    }

                    .input-field {
                        display: flex;
                        flex-direction: column;
                        gap: 6px;
                    }

                    .input-field label {
                        font-size: 10px;
                        font-weight: 700;
                        color: #64748b;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }

                    .input-field input, .input-field select {
                        padding: 10px 14px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 10px;
                        font-size: 14px;
                        color: #1e293b;
                        transition: all 0.2s;
                        outline: none;
                        width: 100%;
                    }

                    .input-field input:focus, .input-field select:focus {
                        border-color: var(--primary-bg-color);
                        background: white;
                        box-shadow: 0 0 0 4px rgba(10, 71, 76, 0.1);
                    }

                    .hint { margin: 0; font-size: 9px; color: #94a3b8; }

                    .select-wrapper { position: relative; width: 100%; }
                    .select-wrapper select { appearance: none; cursor: pointer; padding-right: 40px; }
                    .select-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

                    .attr-dialog-footer {
                        display: flex;
                        gap: 12px;
                        margin-top: 8px;
                    }

                    .attr-dialog-footer button {
                        flex: 1;
                        padding: 12px;
                        border-radius: 10px;
                        font-size: 14px;
                        font-weight: 700;
                        cursor: pointer;
                        transition: all 0.2s;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                    }

                    .btn-cancel {
                        background: white;
                        border: 1px solid #e2e8f0;
                        color: #64748b;
                    }
                    .btn-cancel:hover { background: #f8fafc; border-color: #cbd5e1; }

                    .btn-submit {
                        background: var(--primary-bg-color);
                        border: none;
                        color: white;
                        box-shadow: 0 4px 6px -1px rgba(10, 71, 76, 0.2);
                    }
                    .btn-submit:hover { background: #073539; transform: translateY(-1px); }
                    .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

                    .spin { animation: spin 1s linear infinite; }
                    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                `}</style>
            </div>
        </div>,
        document.body
    );
};

export default CreateAttributeDialog;
